import { useContext } from 'react'
import { useSelector } from 'react-redux'
import { defineMessages, useIntl } from 'react-intl'
import { SectionWrapper, Avatar, Format } from '../../../..'
import { Graph, GreenDollarSign, Calendar } from '../../../../../svgs'
import {
  selectCustomerPhotoUri,
  selectCustomerType,
} from '../../../../../store/selectors/customerProfile'
import { selectHomeValue } from '../../../../../store/selectors/home/metadata'
import { CustomerType } from '../../../../../types/customerType'
import history from '../../../../../history'
import { OVERLAY_HELPERS } from '../../../../../constants/navigation'
import { HOME_IMPROVEMENTS } from '../../../../../constants/gems'
import { selectShouldRenderCashout } from '../../../../../store/selectors/display'
import { Text, Button, Heading } from '@homebot/shared-ui-swashbuckle'

import styles from './PmiInfoMessages.module.scss'
import {
  AmplitudeContext,
  AmplitudeEventConstants,
} from '@homebot/shared-util-quartermaster'

export const MSG = defineMessages({
  contactCustomerTitle: {
    id: 'PMI.panel.contactCustomer.title',
    defaultMessage: 'Talk to your {customerType}',
  },
  contactCustomerText: {
    id: 'PMI.panel.contactCustomer.text',
    defaultMessage:
      "They can help explain your options to remove mortgage insurance. It can get complicated quickly, so it's best to ask for their help. Your servicer may assess the age of your loan, payment history, and more.",
  },
  doubleCheckTitle: {
    id: 'PMI.panel.doubleCheck.title',
    defaultMessage: 'Double-check your home value',
  },
  doubleCheckText: {
    id: 'PMI.panel.doubleCheck.text',
    defaultMessage:
      '{homeValue} is an estimate. You may need to pay for a broker price opinion to qualify for mortgage insurance removal. {expertOpinion}',
  },
  expertOpinion: {
    id: 'PMI.panel.doubleCheck.text.opinion',
    defaultMessage: '(This typically costs around $100 to $200.)',
  },
  improveHomeValueTitle: {
    id: 'PMI.panel.improveHomeValue.title',
    defaultMessage: "Improve your home's value",
  },
  improveHomeValueText: {
    id: 'PMI.panel.improveHomeValue.text',
    defaultMessage:
      'Certain {homeImprovementsLink} may increase your equity percentage.',
  },
  improveHomeValueTextV2: {
    id: 'PMI.panel.improveHomeValue.text.no.cashout',
    defaultMessage:
      'Certain home improvements may increase your equity percentage.',
  },
  improveHomeValueButton: {
    id: 'PMI.panel.improveHomeValue.button',
    defaultMessage: 'home improvements',
  },
  accelerateDateTitle: {
    id: 'PMI.panel.accelerateDate.title',
    defaultMessage: 'Accelerate the date',
  },
  accelerateDateText: {
    id: 'PMI.panel.accelerateDate.text',
    defaultMessage:
      '{payingDownPrincipal} sooner can help increase your net worth quickly, allowing you to potentially remove PMI ahead of schedule.',
  },
  accelerateDateButton: {
    id: 'PMI.panel.accelerateDate.button',
    defaultMessage: 'Paying down principal',
  },
})

export interface InfoSection {
  icon?: React.ReactNode
  title?: string
  text?: string | React.ReactNode
}

const InfoSection = ({ icon, title, text }: InfoSection) => {
  return (
    <div className={styles.infoLayout}>
      <div className={styles.iconContainer}>{icon}</div>
      <div className={styles.infoLayoutText}>
        <Heading as='h4' size='sm'>
          {title}
        </Heading>
        <Text size='sm'>{text}</Text>
      </div>
    </div>
  )
}

export const PmiInfoMessages = () => {
  const intl = useIntl()
  const { ampli } = useContext(AmplitudeContext)

  const photoUri = useSelector(selectCustomerPhotoUri)
  const type = useSelector(selectCustomerType)
  const customerType =
    type === CustomerType.LoanOfficers ? 'loan officer' : 'agent'
  const homeValue = useSelector(selectHomeValue)

  const showCashOutText = useSelector(selectShouldRenderCashout)

  const handleHomeImprovementClick = () => {
    ampli?.pmiInfoRequested({
      inspected_pmi_info: AmplitudeEventConstants.HOME_IMPROVEMENT,
    })
    history.push(OVERLAY_HELPERS.cashout.buildPath(HOME_IMPROVEMENTS))
  }

  const handlePayingDownPrincipalClick = () => {
    ampli?.pmiInfoRequested({
      inspected_pmi_info: AmplitudeEventConstants.PRINCIPAL_PAYMENT,
    })
    history.push(OVERLAY_HELPERS.principalPaymentLearnMore.buildPath())
  }

  const homeImprovementText = showCashOutText
    ? intl.formatMessage(MSG.improveHomeValueText, {
        homeImprovementsLink: (
          <Button
            variant='link'
            size='sm'
            colorScheme='btn-secondary'
            onClick={handleHomeImprovementClick}
          >
            {intl.formatMessage(MSG.improveHomeValueButton)}
          </Button>
        ),
      })
    : intl.formatMessage(MSG.improveHomeValueTextV2)

  return (
    <SectionWrapper>
      <InfoSection
        icon={<Graph className={styles.icon} />}
        title={intl.formatMessage(MSG.doubleCheckTitle)}
        text={intl.formatMessage(MSG.doubleCheckText, {
          homeValue: (
            <Text as='span' color='text.brand' fontWeight='bold'>
              <Format.Currency value={homeValue} />
            </Text>
          ),
          expertOpinion: (
            <Text as='i'>{intl.formatMessage(MSG.expertOpinion)}</Text>
          ),
        })}
      />
      <InfoSection
        icon={<GreenDollarSign className={styles.icon} />}
        title={intl.formatMessage(MSG.improveHomeValueTitle)}
        text={homeImprovementText}
      />
      <InfoSection
        icon={<Calendar className={styles.icon} />}
        title={intl.formatMessage(MSG.accelerateDateTitle)}
        text={intl.formatMessage(MSG.accelerateDateText, {
          payingDownPrincipal: (
            <Button
              variant='link'
              size='sm'
              colorScheme='btn-secondary'
              onClick={handlePayingDownPrincipalClick}
            >
              {intl.formatMessage(MSG.accelerateDateButton)}
            </Button>
          ),
        })}
      />
      <InfoSection
        icon={<Avatar className={styles.icon} src={photoUri} />}
        title={intl.formatMessage(MSG.contactCustomerTitle, {
          customerType: customerType,
        })}
        text={intl.formatMessage(MSG.contactCustomerText)}
      />
    </SectionWrapper>
  )
}
